import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Search, RefreshCw, X, Mail, MailX, MailWarning, HelpCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "all", label: "All Time" },
];

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const EmailBadge = ({ status }) => {
  const map = {
    sent: { t: "Email Sent", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Mail },
    failed: { t: "Email Failed", cls: "bg-red-50 text-red-700 border-red-200", Icon: MailX },
    skipped: { t: "Not Sent", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: MailWarning },
  };
  const s = map[status] || { t: "Unknown", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: HelpCircle };
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {s.t}
    </span>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5">
    <p className="text-xs uppercase tracking-widest font-semibold text-slate-400">{label}</p>
    <p className="mt-2 font-heading text-3xl font-extrabold text-slate-900">{value}</p>
  </div>
);

const DETAIL_FIELDS = [
  ["first_name", "First Name"], ["last_name", "Last Name"], ["company", "Company"],
  ["job_title", "Job Title"], ["email", "Company Email"], ["phone", "Phone"],
  ["company_size", "Company Size"], ["location", "Location / City"], ["industry", "Industry"],
  ["current_it", "Current IT Setup"], ["prompted_by", "What Prompted You"], ["looking_to", "Looking To"],
  ["source", "Source"],
];

export default function AdminLeads() {
  const navigate = useNavigate();
  const [range, setRange] = useState("all");
  const [search, setSearch] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState({ stats: { total: 0, today: 0, last7: 0, last30: 0 }, leads: [], hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("zx_admin_token") : null;

  const logout = useCallback(() => {
    localStorage.removeItem("zx_admin_token");
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  const fetchLeads = useCallback(async (r) => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        params: { range: r, include_test: includeTest },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      setError("Could not load leads. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token, navigate, logout, includeTest]);

  useEffect(() => { fetchLeads(range); }, [range, includeTest, fetchLeads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.leads;
    return data.leads.filter((l) =>
      ["company", "name", "first_name", "last_name", "email", "phone"]
        .some((k) => String(l[k] || "").toLowerCase().includes(q))
    );
  }, [data.leads, search]);

  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">
            Zentryx <span className="text-slate-400">Leads</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={() => fetchLeads(range)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard testid="stat-total" label="Total Leads" value={data.stats.total} />
          <StatCard testid="stat-today" label="Today's Leads" value={data.stats.today} />
          <StatCard testid="stat-7d" label="Last 7 Days" value={data.stats.last7} />
          <StatCard testid="stat-30d" label="Last 30 Days" value={data.stats.last30} />
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4">
          <div className="flex flex-wrap gap-2">
            {RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`filter-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  range === r.key
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="relative sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company, name, email, phone"
              className="w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 transition"
            />
          </div>
        </div>

        {/* Test-record toggle */}
        <label data-testid="toggle-include-test" className="flex items-center gap-2.5 mb-4 cursor-pointer select-none w-fit">
          <input
            type="checkbox"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900/30"
          />
          <span className="text-sm text-slate-600">
            Show test / demo submissions
            {data.hidden > 0 && !includeTest && (
              <span className="ml-1 text-slate-400">({data.hidden} hidden)</span>
            )}
          </span>
        </label>

        {/* Table */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-400">
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Submitted</th>
                  <th className="px-4 py-3 font-semibold">First</th>
                  <th className="px-4 py-3 font-semibold">Last</th>
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Size</th>
                  <th className="px-4 py-3 font-semibold">Industry</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline" /> Loading…
                  </td></tr>
                ) : error ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-red-600">{error}</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} data-testid="admin-empty" className="px-4 py-12 text-center text-slate-400">
                    No production leads found for this filter.
                  </td></tr>
                ) : (
                  filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      onClick={() => setSelected(l)}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500">{fmtDate(l.created_at)}</td>
                      <td className="px-4 py-3 text-slate-900">{l.first_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-900">{l.last_name || "—"}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{l.company || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{l.email || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.company_size || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{l.industry || "—"}</td>
                      <td className="px-4 py-3"><EmailBadge status={l.email_status} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Showing {filtered.length} lead{filtered.length === 1 ? "" : "s"}.{" "}
          {includeTest
            ? "Test/demo records are included."
            : `Test/demo records are hidden${data.hidden ? ` (${data.hidden})` : ""}.`}
        </p>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail-modal">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="font-heading text-lg font-bold text-slate-900">{selected.name || `${selected.first_name} ${selected.last_name}`}</p>
                <p className="text-sm text-slate-500">{selected.company}</p>
              </div>
              <button data-testid="lead-detail-close" onClick={() => setSelected(null)} className="p-2 rounded-full hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest font-semibold text-slate-400">Submitted</span>
                <span className="text-sm text-slate-700">{fmtDate(selected.created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest font-semibold text-slate-400">Email Status</span>
                <EmailBadge status={selected.email_status} />
              </div>
              <div>
                <span className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-1.5">Interested In</span>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.interested_in && selected.interested_in.length)
                    ? selected.interested_in.map((s) => (
                        <span key={s} className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">{s}</span>
                      ))
                    : <span className="text-sm text-slate-400">—</span>}
                </div>
              </div>
              <dl className="grid grid-cols-1 gap-3">
                {DETAIL_FIELDS.map(([k, label]) => (
                  <div key={k} className="flex items-start justify-between gap-4 border-t border-slate-100 pt-3">
                    <dt className="text-xs uppercase tracking-widest font-semibold text-slate-400 flex-shrink-0">{label}</dt>
                    <dd className="text-sm text-slate-800 text-right break-words">{selected[k] || "—"}</dd>
                  </div>
                ))}
                <div className="border-t border-slate-100 pt-3">
                  <dt className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-1.5">Message / Challenges</dt>
                  <dd className="text-sm text-slate-800 whitespace-pre-wrap">{selected.message || "—"}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
